/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useMemo, useRef } from 'react';
import { Partner, PartnerCountry } from '@marageti/z4-sdk/lib/travel';
import {
  Box, Card, Grid, Skeleton, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  ErrorBox, ErrorMessage, ErrorTitle, getYear, LabelAndValueElement, RatingHistogram, useApiClient, useFetchImmutable, useReviewsHistogram,
} from '@marageti/z4-lib';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import ReactCountryFlag from 'react-country-flag';
import { Logo } from '../../../components/avatar';
import { getPartnerPrimaryPhoneNumber, getPartnerUSPhoneNumber, mergeAndSortCountries } from '../../../utils/helpers';

type ProfileCardElementProps = {
  title: string;
  value: any;
};

const CustomLabelAndValueElement = ({
  title,
  value,
}: ProfileCardElementProps) => (value !== undefined ? (
  <Stack>
    <Typography variant="subtitle2" component="span">
      {title}
    </Typography>
    <Typography variant="body1" color="text.secondary" component="span">
      {value}
    </Typography>
  </Stack>
) : (
  <Skeleton
    variant="rounded"
    animation="wave"
    sx={{
      margin: '0 auto',
      height: '44px',
      width: '100%',
    }}
  />
));

const TruncatedTooltip = ({ text }: { text: string }) => {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  useEffect(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [text]);

  return (
    <Tooltip title={text} disableHoverListener={!isTruncated}>
      <Typography ref={textRef} variant="body2" noWrap component="span">
        {text}
      </Typography>
    </Tooltip>
  );
};

interface CountryFlagSectionProps {
  servedCountries: PartnerCountry[] | undefined;
}

const CountryFlagSection = ({ servedCountries }: CountryFlagSectionProps) => {
  const apiClient = useApiClient();
  const { data: countryCodes } = useFetchImmutable<CountryCodeMap>(
    apiClient.standardsClient.getCountryCodes,
    [],
    true,
  );

  if (!servedCountries?.length) {
    return null;
  }

  const itemsPerColumn = Math.ceil(servedCountries.length / 2);
  const firstColumn = servedCountries.slice(0, itemsPerColumn);
  const secondColumn = servedCountries.slice(itemsPerColumn);

  return (
    <Grid container spacing={2} sx={{ pt: 1 }}>
      {/* Left column */}
      <Grid item xs={6}>
        {firstColumn.map((country) => (
          <Box
            key={country.country}
            sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
          >
            <ReactCountryFlag
              style={{ width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' }}
              countryCode={country.country}
              svg
            />
            <TruncatedTooltip text={countryCodes?.[country.country] ?? ''} />
          </Box>
        ))}
      </Grid>

      {/* Right column */}
      <Grid item xs={6}>
        {secondColumn.map((country) => (
          <Box
            key={country.country}
            sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
          >
            <ReactCountryFlag
              style={{ width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' }}
              countryCode={country.country}
              svg
            />
            <Typography variant="body2" noWrap component="span">
              {countryCodes?.[country.country]}
            </Typography>
          </Box>
        ))}
      </Grid>
    </Grid>
  );
};
interface HistogramSectionProps {
  partnerId: string | undefined;
}

const HistogramSection = ({
  partnerId,
}: HistogramSectionProps) => {
  const { histogramAggregation, loading: loadingHistogram, error: errorHistogram } = useReviewsHistogram({ partnerId });
  if (loadingHistogram || !histogramAggregation) {
    return <Skeleton variant="rounded" animation="wave" sx={{ width: '100%', height: '44px' }} />;
  }

  if (errorHistogram) {
    return (
      <ErrorBox>
        <ErrorTitle>There was an error loading the histogram</ErrorTitle>
        <ErrorMessage>
          {`Code: ${errorHistogram.code}: ${errorHistogram.message}`}
        </ErrorMessage>
      </ErrorBox>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: {
          desktop: '100%',
          mobile: '400px',
        },
        mb: 1,
        mt: {
          desktop: 1,
          mobile: 0,
        },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        alignSelf: 'center',
      }}
    >
      <RatingHistogram.ShortSummary
        aggregation={histogramAggregation}
        sx={{
          maxWidth: '100%',
        }}
      />
    </Box>
  );
};

interface ProfileCardProps {
  partner: Partner | undefined;
  hideWebsite?: boolean;
}

const ProfileCard = ({
  partner, hideWebsite = false,
}: ProfileCardProps) => {
  const apiClient = useApiClient();
  const {
    data: countryCodes,
  } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);

  const officeLocation = useMemo(() => {
    if (!partner?.offices?.length) {
      return '-';
    }

    const office = partner.offices[0];
    if (!office.city || !countryCodes?.[office.country]) {
      return '-';
    }

    return `${office.city}, ${countryCodes[office.country]}`;
  }, [partner?.offices, countryCodes]);

  const mergedCountries = useMemo(() => mergeAndSortCountries(partner?.primaryCountries, partner?.secondaryCountries), [partner?.primaryCountries, partner?.secondaryCountries]);

  return (
    <Card>
      <Box
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
        }}
        >
          <Logo
            alt={partner?.name}
            diameter={138}
            loading={!partner}
            src={partner?.logo && `data:image/png;base64,${partner.logo}`}
          >
            {partner?.name[0]}
          </Logo>

        </Box>
        <Typography variant="bodyBold" sx={{ textAlign: 'center', mt: 2 }}>
          {partner?.name}
        </Typography>
        <HistogramSection
          partnerId={partner?.id}
        />
        <Stack spacing="24px" width="100%" sx={{ pt: 3 }}>
          <LabelAndValueElement
            title="Zicasso Partner Since"
            value={partner?.onboardedOn ? getYear(partner?.onboardedOn) : '-'}
            key="zicasso-partner-since"
          />
          <LabelAndValueElement
            title="US Phone"
            value={getPartnerUSPhoneNumber(partner)}
            key="us-phone"
          />
          <LabelAndValueElement
            title="Primary Phone"
            value={getPartnerPrimaryPhoneNumber(partner)}
            key="primary-phone"
          />
          {!hideWebsite
          && (
          <LabelAndValueElement
            title="Website"
            value={partner?.website || '-'}
            key="website"
          />
          )}
          <LabelAndValueElement
            title="Location"
            value={partner?.offices?.[0] ? officeLocation : '-'}
            key="location"
          />
          <CustomLabelAndValueElement
            title="Specialized Destinations"
            value={
              partner?.primaryCountries ? (<CountryFlagSection servedCountries={mergedCountries} />) : ('-')
            }
            key="specialized-destinations"
          />
        </Stack>
      </Box>
    </Card>
  );
};

export default ProfileCard;
